import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { numeDepartament } from '@b4l/shared'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { useConfirmare } from '../../components/ui/Confirmare'

interface Propunere {
  id: number
  titlu: string
  din: string | null
  spre: string | null
  motiv: string
}

/**
 * Retrierea task-urilor „De preluat" venite din email, cu noul filtru (14.10.2026). Intai lista
 * — ce se muta, ce se arhiveaza si de ce —, apoi „Aplică" doar pe ce ramane bifat. Doar admin.
 */
export function RetriereTaskuri() {
  const { confirma } = useConfirmare()
  const [lista, setLista] = useState<Propunere[] | null>(null)
  const [scoase, setScoase] = useState<Set<number>>(new Set())

  const previzualizeaza = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ propuneri: Propunere[]; totale: number; neclasificate: number }>(
          '/tasks/extractor/retriere/previzualizare',
          {},
          { timeout: 300_000 },
        )
      ).data,
    onSuccess: (d) => {
      setLista(d.propuneri)
      setScoase(new Set())
      if (d.neclasificate > 0)
        toast.warning(`${d.neclasificate} task-uri n-au putut fi reclasificate; rămân cum sunt.`)
    },
    onError: () => toast.error('Previzualizarea n-a mers. Încearcă din nou.'),
  })
  const aplica = useMutation({
    mutationFn: async (decizii: Propunere[]) =>
      (
        await api.post<{ mutate: number; arhivate: number; sarite: number }>(
          '/tasks/extractor/retriere/aplica',
          { decizii: decizii.map((d) => ({ id: d.id, spre: d.spre, motiv: d.motiv })) },
        )
      ).data,
    onSuccess: (r) => {
      toast.success(
        `Gata: ${r.mutate} mutate, ${r.arhivate} arhivate${r.sarite ? `, ${r.sarite} sărite (le preluase cineva)` : ''}.`,
      )
      setLista(null)
    },
    onError: () => toast.error('Aplicarea n-a mers.'),
  })

  const deSchimbat = useMemo(() => (lista ?? []).filter((p) => p.spre !== p.din), [lista])
  const alese = deSchimbat.filter((p) => !scoase.has(p.id))
  const raman = (lista ?? []).length - deSchimbat.length

  return (
    <Card
      title="Retriază task-urile din email"
      description="Trece task-urile „De preluat” venite din email prin filtrul nou: cele fără acțiune se arhivează, cele încadrate greșit se mută. Vezi lista înainte; se aplică doar ce lași bifat."
    >
      <div className="space-y-3">
        <Button
          variant="secondary"
          onClick={() => previzualizeaza.mutate()}
          loading={previzualizeaza.isPending}
        >
          Previzualizează
        </Button>
        {lista && (
          <>
            <p className="text-xs text-b4l-text-muted">
              {deSchimbat.length === 0
                ? `Nimic de schimbat — toate cele ${lista.length} sunt unde trebuie.`
                : `${deSchimbat.length} de schimbat, ${raman} rămân cum sunt.`}
            </p>
            {deSchimbat.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-b4l-text-dim">
                      <th className="w-8 py-1" />
                      <th className="py-1">Task</th>
                      <th className="py-1">Acum</th>
                      <th className="py-1">Devine</th>
                      <th className="py-1">De ce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deSchimbat.map((p) => (
                      <tr key={p.id} className="border-t border-b4l-border align-top">
                        <td className="py-1.5">
                          <input
                            type="checkbox"
                            aria-label={`Aplică pentru „${p.titlu}”`}
                            checked={!scoase.has(p.id)}
                            onChange={() =>
                              setScoase((s) => {
                                const n = new Set(s)
                                if (n.has(p.id)) n.delete(p.id)
                                else n.add(p.id)
                                return n
                              })
                            }
                          />
                        </td>
                        <td className="py-1.5 pr-3 text-b4l-text">{p.titlu}</td>
                        <td className="whitespace-nowrap py-1.5 pr-3">
                          {p.din ? numeDepartament(p.din) : '—'}
                        </td>
                        <td
                          className={`whitespace-nowrap py-1.5 pr-3 font-semibold ${p.spre ? 'text-b4l-blue-dark' : 'text-b4l-text-muted'}`}
                        >
                          {p.spre ? numeDepartament(p.spre) : 'arhivat'}
                        </td>
                        <td className="py-1.5 text-b4l-text-muted">{p.motiv}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {alese.length > 0 && (
              <Button
                loading={aplica.isPending}
                onClick={async () => {
                  if (
                    await confirma({
                      titlu: `Aplici ${alese.length} schimbări?`,
                      mesaj:
                        'Task-urile bifate se mută sau se arhivează. Fiecare schimbare rămâne scrisă în traseul task-ului. Cele preluate între timp nu se ating.',
                      buton: 'Aplică',
                    })
                  )
                    aplica.mutate(alese)
                }}
              >
                Aplică ({alese.length})
              </Button>
            )}
          </>
        )}
      </div>
    </Card>
  )
}
